import { 
  StatisticsType, 
  StatComparisionType, 
  TriggerableStatInput 
}  from 'src/services/graphql/Models';
import Filter from './Filter';
import Trigger from './Trigger';
import UnitSensor from 'src/stores/application/classes/UnitSensor';
import { DataClass } from 'src/shared/utility';
import { Column } from 'react-data-grid';
import FixedNumber from 'src/components/FixedNumber';

const { Filters: { NumericFilter } } = require('react-data-grid-addons');

export default class TriggerableStat implements TriggerableStatInput, DataClass {
  clientId: string;
  typedId: string;
  statIndex: number;
  name?: string | null;
  associatedChart?: string | null;
  sensorType?: string | null;
  measurementUnit?: string | null;
  include?: Array< Filter | null > | null;
  exclude?: Array< Filter | null > | null;
  statType: StatisticsType;
  comparisionType: StatComparisionType;
  comparedToStatType?: StatisticsType | null;
  comparisionValue?: number | null;
  triggers?: Array< Trigger > | null;
  
  constructor ( from: any ) { 
    this.clientId = from.clientId;
    this.typedId = from.typedId;
    this.statIndex = from.statIndex;
    this.name = from.name;
    this.associatedChart = from.associatedChart;
    this.sensorType = from.sensorType;
    this.measurementUnit = from.measurementUnit;
    if (from.include && from.include.slice) {
      let include = from.include.slice();
      this.include = include.map(o => new Filter(o));
    } else {
      this.include = [];
    }
    if (from.exclude && from.exclude.slice) {
      let exclude = from.exclude.slice();
      this.exclude = exclude.map(o => new Filter(o));
    } else {
      this.exclude = [];
    }
    this.statType = from.statType;
    this.comparisionType = from.comparisionType;
    this.comparedToStatType = from.comparedToStatType;
    this.comparisionValue = from.comparisionValue;
    this.triggers = ( from.triggers || []).map(o => new Trigger(o));
  }

  public getComparedStatVal(unitSensorStats: UnitSensor) {
    let initialValue = this.getStatVal(unitSensorStats, this.statType);
    if (this.comparisionType === StatComparisionType.notCompared && this.comparisionValue) {
      return initialValue;
    }
    
    let comparedStats = new UnitSensor({
        ...unitSensorStats.parseAbsolute(), 
        lastValue: isNaN(this.comparisionValue) ? 
            unitSensorStats.lastValue : this.comparisionValue 
    });
  
    let comparedValue = this.getStatVal(comparedStats, this.comparedToStatType);

    switch (this.comparisionType) {
      case StatComparisionType.addition:
        return initialValue + comparedValue;

      case StatComparisionType.subtraction:
        return comparedValue - initialValue;

      case StatComparisionType.multiplication:
        return initialValue * comparedValue;

      case StatComparisionType.division:
        return initialValue / comparedValue;

      case StatComparisionType.percentage:
        return initialValue / comparedValue * 100;

      default:
        return initialValue;
    }
  }

  public extractValues(unitSensors: UnitSensor[]) {
    return unitSensors.map(unitSensor => this.getComparedStatVal(unitSensor));
  }

  public getStatName(stat: StatisticsType) {
    switch (stat) {
      case StatisticsType.average:
        return 'Average';
      case StatisticsType.max:
        return 'Maximux';
      case StatisticsType.min:
        return 'Minimum';
      case StatisticsType.total:
        return 'Total';
      default:
        return 'Value';
    }
  }

  public getAgainstStatName(stat: StatisticsType) {
    let result = this.getStatName(stat);
    if (result === 'Value') {
      return this.comparisionValue;
    } else {
      return result.toLowerCase();
    }
  }

  public getComparisionType(comp: StatComparisionType) {
    switch (comp) {
      case StatComparisionType.addition:
        return 'plus';
      case StatComparisionType.division:
        return 'divided by ';
      case StatComparisionType.multiplication:
        return 'multiplied by ';
      case StatComparisionType.percentage:
        return 'percentage of';
      case StatComparisionType.subtraction:
        return 'difference from ';
      default:
        return false;
    }

  }

  public getName(): string {
    let initialStatName = this.getStatName(this.statType);
    let compResult = this.getComparisionType(this.comparisionType);

    if (compResult === false) {
      return initialStatName;
    }
    
    return `${initialStatName} ${compResult} ${this.getAgainstStatName(this.comparedToStatType as StatisticsType)}`;
  }

  public toColumn(): Column {
    return {
      key: this.typedId,
      name: this.name,
      sortable: true,
      filterable: true,
      filterRenderer: NumericFilter,
      formatter: FixedNumber
    };
  }

  parseAbsolute() {
    return {  
      clientId: this.clientId,
      typedId: this.typedId,
      statIndex: this.statIndex,
      name: this.name,
      associatedChart: this.associatedChart,
      sensorType: this.sensorType,
      measurementUnit: this.measurementUnit,
      include: ( this.include  || []).map(o => o.parseAbsolute()),
      exclude: ( this.exclude ||  []).map(o => o.parseAbsolute()),
      statType: this.statType,
      comparisionType: this.comparisionType,
      comparedToStatType: this.comparedToStatType,
      comparisionValue: this.comparisionValue,
      triggers: ( this.triggers || []).map(o => o.parseAbsolute())
    };
  }

  private getStatVal(unitSensorStats: UnitSensor, statType?: StatisticsType) {
    let { min, max, average, lastValue, total } = unitSensorStats;
    switch (statType) {
      case StatisticsType.total:
        return total;
      case StatisticsType.average:
        return average;
      case StatisticsType.min:
        return min;
      case StatisticsType.max:
        return max;
      default:
        return lastValue;
    }
  }
}


// WEBPACK FOOTER //
// ./src/stores/application/classes/TriggerableStat.ts